import type { IamV1LoginReply, SharingGetShareDataData, UserInfo } from '@/app/types';
// https://shengshu.feishu.cn/wiki/Wf3hwPgwWieR0Ak6cnTcKTjVnTh?fromScene=spaceOverview
import { initFromUserAgent, initSniff, type SniffType } from './init-from-user-agent';

type CallbackResult = {
  key: string;
  value: unknown;
};

type BridgeParams = Record<string, unknown> | IamV1LoginReply | string[] | { url: string };
type BridgeMethod = (params?: BridgeParams) => void;

declare global {
  interface Window {
    appCommonCallback?: (result: string) => void;
    priviledgeCallback?: (result: string) => void;
    payCallback?: (result: string) => boolean;
    SSBridge: {
      checkAppPriviledge?: (val: string[]) => void;
      close?: () => void;
      isLogin?: () => boolean;
      getUserInfo?: () => UserInfo;
      getToken?: () => string;
      getStatusBarHeight?: () => number;
      saveLogin?: (info: string) => void;
      getInvitationId?: () => string;
      getInviterBizType?: () => string;
      share?: (payload: string) => void;
      toLogin?: () => void;
      toSubscribe?: () => boolean;
      toPrivacyPolicy?: () => void;
      toUserAgreement?: () => void;
      openCamera?: () => void;
      chooseImage?: () => void;
      download?: (url: string) => void;
      [key: string]: unknown;
    };
    webkit?: {
      messageHandlers?: {
        checkAppPriviledge?: {
          postMessage: (message: string) => void;
        };
        close?: {
          postMessage: (message: string) => void;
        };
        isLogin?: {
          postMessage: (message: string) => void;
        };
        getUserInfo?: {
          postMessage: (message: string) => void;
        };
        getToken?: {
          postMessage: (message: string) => void;
        };
        getStatusBarHeight?: {
          postMessage: (message: string) => void;
        };
        saveLogin?: {
          postMessage: (message: string) => void;
        };
        getInvitationId?: {
          postMessage: (message: string) => void;
        };
        getInviterBizType?: {
          postMessage: (message: string) => void;
        };
        share?: {
          postMessage: (message: string) => void;
        };
        toLogin?: {
          postMessage: (message: string) => void;
        };
        toSubscribe?: {
          postMessage: (message: string) => void;
        };
        toPrivacyPolicy?: {
          postMessage: (message: string) => void;
        };
        toUserAgreement?: {
          postMessage: (message: string) => void;
        };
        openCamera?: {
          postMessage: (message: string) => void;
        };
        chooseImage?: {
          postMessage: (message: string) => void;
        };
        download?: {
          postMessage: (message: string) => void;
        };
        [key: string]:
          | {
              postMessage: (message: string) => void;
            }
          | undefined;
      };
    };
  }
}

class HySdk {
  sniff: SniffType = initSniff;

  // 初始化客户端特定的逻辑
  initClient() {
    if (typeof window !== 'undefined') {
      this.sniff = initFromUserAgent(window.navigator.userAgent);
    }
  }

  // 调用app购买
  onPurchase(data: {
    options: {
      uid: string; // userid
    };
    onSuccess: (result: object) => void;
  }) {
    const { options, onSuccess } = data;

    window.location.href = `vidu://products?uid=${options.uid}`;
    window.payCallback = (result: string) => {
      const resultObj = JSON.parse(result);
      onSuccess(resultObj);
      return true;
    };
  }

  /**
   * 通用桥接调用方法
   * @param methodName 方法名称
   * @param params 可选参数
   * @returns Promise
   */
  private callBridge = <T>(methodName: string, params?: BridgeParams): Promise<T> => {
    return new Promise((resolve, reject) => {
      // 确保SSBridge对象存在
      if (typeof window !== 'undefined' && !window.SSBridge) {
        window.SSBridge = {};
      }

      try {
        // 设置桥接方法
        window.SSBridge[methodName] = ((passedParams?: BridgeParams) => {
          const messageData = params || passedParams || {};
          const webkitHandler =
            window.webkit?.messageHandlers?.[methodName as keyof typeof window.webkit.messageHandlers];
          webkitHandler?.postMessage(JSON.stringify(messageData));
        }) as unknown as BridgeMethod;

        // 设置回调处理
        window.appCommonCallback = (result: string) => {
          try {
            const res: CallbackResult = JSON.parse(result);
            if (res.key === methodName) {
              resolve(res.value as T);
            }
          } catch (error) {
            reject(error);
          }
        };

        // 调用桥接方法
        const bridgeMethod = window.SSBridge[methodName] as BridgeMethod | undefined;
        if (bridgeMethod) {
          bridgeMethod(params);
        } else {
          reject(new Error(`${methodName} not available`));
        }
      } catch (error) {
        console.error(`${methodName} error`, error);
        reject(error);
      }
    });
  };

  // 获取应用权限信息 只在ios上用过
  getAppPermissionInfo = (val: string[]): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      // 确保SSBridge对象存在
      if (typeof window !== 'undefined' && !window.SSBridge) {
        window.SSBridge = {};
      }

      try {
        // 发送请求 => web端触发
        window.SSBridge.checkAppPriviledge = (val: string[]) => {
          window.webkit?.messageHandlers?.checkAppPriviledge?.postMessage(JSON.stringify({ type: val.join(',') }));
        };
        // 接收回调 => 客户端触发
        window.priviledgeCallback = (result: string) => {
          const res = JSON.parse(result) || [];
          if (res.every((item: { type: string; hasPri: boolean }) => val.includes(item.type))) {
            resolve(res.every((item: { hasPri: boolean }) => item.hasPri));
          } else {
            reject(new Error('no permission'));
          }
        };

        // 调用checkAppPriviledge
        if (window.SSBridge.checkAppPriviledge) {
          window.SSBridge.checkAppPriviledge(val);
        } else {
          reject(new Error('checkAppPriviledge not available'));
        }
      } catch (error) {
        console.error('getAppPermissionInfo error', error);
      }
    });
  };

  /**
   * 关闭当前会话/释放资源
   */
  closeWebView = (): Promise<void> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.close) {
        window.SSBridge.close();
      }
    }
    return this.callBridge<void>('close');
  };

  /**
   * 检查用户登录状态返回 true 表示已登录
   */
  isLogin = (): Promise<boolean> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.isLogin) {
        return new Promise((resolve) => {
          const result = window.SSBridge?.isLogin?.();
          resolve(!!result);
        });
      }
    }
    return this.callBridge<boolean>('isLogin');
  };

  /**
   * 获取用户信息（JSON字符串,可能为空）
   */
  getUserInfo = (): Promise<UserInfo> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.isLogin) {
        return new Promise((resolve) => {
          const result = window.SSBridge?.getUserInfo?.();
          resolve(result as UserInfo);
        });
      }
    }
    return this.callBridge<UserInfo>('getUserInfo');
  };

  /**
   * 获取用户认证令牌（可能为空）
   */
  getToken = (): Promise<string> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.getToken) {
        return new Promise((resolve) => {
          const result = window.SSBridge?.getToken?.();
          resolve(result as string);
        });
      }
    }
    return this.callBridge<string>('getToken');
  };

  /**
   * 状态栏高度(未使用)
   * TODO: 这个不应该只获取状态栏高度。  底部bottom高度之类的也可以从这取。
   */
  getStatusBarHeight = (): Promise<number> => {
    return this.callBridge<number>('getStatusBarHeight');
  };

  /**
   * 保存登录凭证（JSON用户对象）
   */
  saveLoginToApp = (info: IamV1LoginReply): Promise<void> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.saveLogin) {
        window.SSBridge.saveLogin(JSON.stringify(info));
      }
    }
    return this.callBridge<void>('saveLogin', info);
  };

  /**
   * 获取邀请ID（用于邀请场景）待定
   */
  getInvitationId = (): Promise<string> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.getInvitationId) {
        return new Promise((resolve) => {
          const result = window.SSBridge?.getInvitationId?.();
          resolve(result as string);
        });
      }
    }
    return this.callBridge<string>('getInvitationId');
  };
  /**
   * 获取邀请类型（用于邀请场景）
   * @returns
   */
  getInviterBizType = (): Promise<string> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.getInviterBizType) {
        return new Promise((resolve) => {
          const result = window.SSBridge?.getInviterBizType?.();
          resolve(result as string);
        });
      }
    }
    return this.callBridge<string>('getInviterBizType');
  };
  /**
   * 触发内容分享功能
   */
  share = (payload: SharingGetShareDataData['query']): Promise<void> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.share) {
        window.SSBridge.share(JSON.stringify(payload));
      }
    }
    return this.callBridge<void>('share', payload);
  };

  /**
   * 跳转至登录界面（预留方法）
   */
  toLogin = (): Promise<void> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.toLogin) {
        window.SSBridge.toLogin();
      }
    }
    return this.callBridge<void>('toLogin');
  };

  /**
   * 跳转至订阅界面
   */
  toSubscribe = (): Promise<boolean> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.toSubscribe) {
        window.SSBridge.toSubscribe();
      }
    }
    return this.callBridge<boolean>('toSubscribe');
  };

  /**
   * 跳转至隐私政策
   */
  toPrivacyPolicy = (): Promise<void> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.toPrivacyPolicy) {
        window.SSBridge.toPrivacyPolicy();
      }
    }
    return this.callBridge<void>('toPrivacyPolicy');
  };

  /**
   * 跳转至用户协议
   */
  toUserAgreement = (): Promise<void> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.toUserAgreement) {
        window.SSBridge.toUserAgreement();
      }
    }
    return this.callBridge<void>('toUserAgreement');
  };

  /**
   * 拍照
   */
  openCamera = (): Promise<void> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.openCamera) {
        window.SSBridge.openCamera();
      }
    }
    return this.callBridge<void>('openCamera');
  };

  /**
   * 相册
   */
  chooseImage = (): Promise<string> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.chooseImage) {
        window.SSBridge.chooseImage();
      }
    }
    return this.callBridge<string>('chooseImage');
  };

  /**
   * 下载图片
   */
  download = (url: string): Promise<void> => {
    if (this.sniff.os === 'android') {
      if (typeof window !== 'undefined' && window.SSBridge?.download) {
        window.SSBridge.download(url);
      }
    }
    return this.callBridge<void>('download', { url });
  };
}

// 创建一个基础实例
const hysdk = new HySdk();

// 在客户端初始化
if (typeof window !== 'undefined') {
  hysdk.initClient();
}

export { hysdk };
